// Staff Onboarding & IT Induction — the IT admin's screen.
// List of requests, and a detail view that walks the flow in order:
// account → groups → induction checklist → complete → staff → quiz → close.
import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { useAuth } from '../context/AuthContext';

const STATUS_LABEL = {
  new_request:'New request', account_setup:'Account setup', access_setup:'Access setup',
  induction_pending:'Induction pending', staff_action_pending:'Staff action pending',
  it_action_pending:'IT action pending', ready_to_close:'Ready to close',
  closed:'Closed', blocked:'Blocked',
};
const STATUS_CLASS = {
  ready_to_close:'badge-completed', closed:'badge-completed',
  blocked:'badge-high', staff_action_pending:'badge-normal', it_action_pending:'badge-high',
};
const dt = v => v ? new Date(v).toLocaleString('en-IN', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' }) : '';
const BOARDS = ['CBSE', 'ICSE', 'State Board', 'IGCSE / Cambridge', 'IB'];

// Always a string. An error payload that turns out to be an object used to
// render as "[object Object]", which tells the person nothing at all.
function errText(e, fallback = 'Something went wrong') {
  const d = e?.response?.data;
  const pick = v => {
    if (!v) return '';
    if (typeof v === 'string') return v;
    if (typeof v === 'object') return v.message || v.error || JSON.stringify(v).slice(0, 300);
    return String(v);
  };
  return pick(d?.error) || pick(d?.message) || pick(d) || e?.message || fallback;
}

// kind='staff' | 'student' — the same screen, two menus. A student has no
// equipment to issue and no HR signature; they record their own laptop instead.
export default function Onboarding({ openId, onOpened, kind = 'staff' }) {
  const isStudent = kind === 'student';
  const { can } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const [filter, setFilter] = useState({ open:'1', search:'' });

  const load = useCallback(() => {
    setLoading(true);
    const params = { kind }; Object.entries(filter).forEach(([k,v]) => { if (v) params[k]=v; });
    api.get('/onboarding', { params }).then(r => setRows(r.data)).catch(()=>{}).finally(()=>setLoading(false));
  }, [filter, kind]);
  useEffect(() => { load(); }, [load]);

  // The home-page button can deep-link straight into "new".
  useEffect(() => { if (openId === 'new') { setNewOpen(true); onOpened && onOpened(); } }, [openId, onOpened]);

  if (sel) return <Detail id={sel} onBack={() => { setSel(null); load(); }} />;

  return (
    <div>
      <div className="flex-gap" style={{ justifyContent:'space-between', alignItems:'center', marginBottom:14, flexWrap:'wrap', gap:10 }}>
        <div>
          <h2 style={{ fontSize:18, fontWeight:600, marginBottom:4 }}>
            {isStudent ? 'Student Onboarding' : 'Staff Onboarding & IT Induction'}</h2>
          <p style={{ fontSize:13, color:'var(--text2)' }}>
            {isStudent
              ? 'Google account, ERP and MyConcern access, the IT policy, and the student’s own laptop details.'
              : 'From the HR mail to a closed, auditable record — account, groups, induction, policy quiz.'}
          </p>
        </div>
        {can('onboarding','create') &&
          <button className="btn btn-primary" onClick={() => setNewOpen(true)}>
            {isStudent ? '+ New Student Onboard' : '+ New Staff Onboard'}</button>}
      </div>

      <div className="card" style={{ marginBottom:14, padding:12 }}>
        <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr auto', gap:10, alignItems:'end' }}>
          <div><label style={{ fontSize:11, color:'var(--text2)' }}>Search name, reference or email</label>
            <input value={filter.search} onChange={e => setFilter(f=>({...f,search:e.target.value}))} /></div>
          <div><label style={{ fontSize:11, color:'var(--text2)' }}>Show</label>
            <select value={filter.open} onChange={e => setFilter(f=>({...f,open:e.target.value}))}>
              <option value="1">Open only</option><option value="">All</option>
            </select></div>
          <button className="btn btn-secondary" onClick={load}>Refresh</button>
        </div>
      </div>

      <div className="card" style={{ padding:0, overflow:'hidden' }}>
        {loading ? <div style={{ padding:40, textAlign:'center', color:'var(--text2)' }}>Loading…</div>
        : !rows.length ? <div className="empty-state"><p>No onboarding requests yet.</p></div>
        : <div className="table-wrap"><table>
            <thead><tr><th>Reference</th><th>Staff</th><th>Campus</th><th>School email</th><th>Joining</th><th>Status</th></tr></thead>
            <tbody>{rows.map(r => (
              <tr key={r.id} style={{ cursor:'pointer' }} onClick={() => setSel(r.id)}>
                <td style={{ fontSize:12.5 }} className="mono">{r.ref}</td>
                <td style={{ fontSize:13 }}>{r.staff_name}
                  {r.is_replacement && <span className="badge badge-normal" style={{ marginLeft:6 }}>replacement</span>}</td>
                <td style={{ fontSize:12.5 }}>{r.campus_name}</td>
                <td style={{ fontSize:12.5 }}>{r.workspace_email || <span style={{ color:'var(--text3)' }}>—</span>}
                  {r.account_simulated && <span className="badge badge-normal" style={{ marginLeft:6 }}>test</span>}</td>
                <td style={{ fontSize:12.5 }}>{r.joining_date ? new Date(r.joining_date).toLocaleDateString('en-IN',{day:'2-digit',month:'short'}) : '—'}</td>
                <td><span className={`badge ${STATUS_CLASS[r.status] || 'badge-normal'}`}>{STATUS_LABEL[r.status] || r.status}</span></td>
              </tr>
            ))}</tbody>
          </table></div>}
      </div>

      {newOpen && <NewRequest kind={kind} onClose={() => setNewOpen(false)} onCreated={id => { setNewOpen(false); setSel(id); }} />}
    </div>
  );
}

// ── New request: paste the HR mail, confirm the details ───────────────────
function NewRequest({ onClose, onCreated, kind = 'staff' }) {
  const isStudent = kind === 'student';
  const [f, setF] = useState({ campus_id:'', department_id:'', staff_name:'', personal_email:'',
    phone:'', designation:'', joining_date:'', hr_text:'', hr_reference:'',
    is_replacement:false, outgoing_name:'', outgoing_email:'', first_name:'', last_name:'',
    // student fields
    id_number:'', grade:'', section:'', board:'', father_contact:'', mother_contact:'' });
  const [campuses, setCampuses] = useState([]);
  const [depts, setDepts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const set = (k,v) => setF(p => ({ ...p, [k]:v }));

  useEffect(() => {
    api.get('/master/campuses').then(r => setCampuses(r.data)).catch(()=>{});
    if (!isStudent) api.get('/master/departments').then(r => setDepts(r.data)).catch(()=>{});
  }, [isStudent]);


  // Best-effort extraction from a pasted HR mail. Everything stays editable —
  // nothing is saved without the admin confirming it on screen.
  const parse = () => {
    const t = f.hr_text || '';
    const grab = re => { const m = t.match(re); return m ? m[1].trim() : ''; };
    const email = grab(/([A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,})/);
    const name = grab(/\b(?:name|staff name|employee)\s*[:\-]\s*(.+)/i);
    const desig = grab(/\b(?:designation|role|post)\s*[:\-]\s*(.+)/i);
    const phone = grab(/\b(?:phone|mobile|contact)\s*[:\-]\s*([0-9+\s-]{8,})/i);
    const date = grab(/\b(?:joining date|date of joining|doj|joining)\s*[:\-]\s*([0-9]{1,4}[-/][0-9]{1,2}[-/][0-9]{1,4})/i);
    const dept = grab(/\b(?:department|dept)\s*[:\-]\s*(.+)/i);
    setF(p => ({ ...p,
      // The mail is one line of text, so split it on the first space and let
      // the admin correct it — which is what the two fields are for.
      first_name: p.first_name || name.split(/\s+/)[0] || '',
      last_name:  p.last_name  || name.split(/\s+/).slice(1).join(' ') || '',
      personal_email: p.personal_email || email,
      designation: p.designation || desig,
      phone: p.phone || phone,
      joining_date: p.joining_date || normDate(date),
      department_id: p.department_id ||
        (depts.find(d => dept && d.name.toLowerCase() === dept.toLowerCase())?.id || ''),
      is_replacement: p.is_replacement || /replacement|replacing/i.test(t),
    }));
  };
  const normDate = s => {
    if (!s) return '';
    const p = s.split(/[-/]/).map(x => x.trim());
    if (p.length !== 3) return '';
    // 2026-11-03 stays; 03-11-2026 flips. Day-first is the local convention.
    return p[0].length === 4 ? `${p[0]}-${p[1].padStart(2,'0')}-${p[2].padStart(2,'0')}`
                             : `${p[2]}-${p[1].padStart(2,'0')}-${p[0].padStart(2,'0')}`;
  };

  const save = async () => {
    setBusy(true); setErr('');
    try { const r = await api.post('/onboarding', { ...f, kind }); onCreated(r.data.id); }
    catch (e) { setErr(errText(e, 'Could not create the request')); }
    finally { setBusy(false); }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" style={{ maxWidth:720 }} onClick={e => e.stopPropagation()}>
        <h3 style={{ marginTop:0 }}>{isStudent ? 'New Student Onboard' : 'New Staff Onboard'}</h3>

        {/* A student being onboarded is new, so the details are entered here
            and the student record is created from them. */}
        {isStudent && (
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
            <div className="form-group"><label>Student name *</label>
              <input value={f.staff_name} onChange={e => set('staff_name', e.target.value)} /></div>
            <div className="form-group"><label>Admission number</label>
              <input value={f.id_number} onChange={e => set('id_number', e.target.value)} placeholder="e.g. CS-26-104" />
              <span style={{ fontSize:11, color:'var(--text2)' }}>
                If this number already exists at the campus, that student is updated rather than duplicated.
              </span></div>
            <div className="form-group"><label>Campus *</label>
              <select value={f.campus_id} onChange={e => set('campus_id', e.target.value)}>
                <option value="">— select —</option>
                {campuses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select></div>
            <div className="form-group"><label>Board</label>
              <select value={f.board} onChange={e => set('board', e.target.value)}>
                <option value="">— select —</option>
                {BOARDS.map(b => <option key={b} value={b}>{b}</option>)}
              </select></div>
            <div className="form-group"><label>Grade</label>
              <select value={f.grade} onChange={e => set('grade', e.target.value)}>
                <option value="">— select —</option>
                {Array.from({ length:12 }, (_,i) => String(i+1)).map(g =>
                  <option key={g} value={g}>{g}</option>)}
              </select>
              <span style={{ fontSize:11, color:'var(--text2)' }}>
                The laptop record applies from grade 6 upward.
              </span></div>
            <div className="form-group"><label>Section</label>
              <input value={f.section} onChange={e => set('section', e.target.value)} placeholder="A" /></div>
            <div className="form-group"><label>Father's contact</label>
              <input value={f.father_contact} onChange={e => set('father_contact', e.target.value)} /></div>
            <div className="form-group"><label>Mother's contact</label>
              <input value={f.mother_contact} onChange={e => set('mother_contact', e.target.value)} /></div>
          </div>
        )}

        {!isStudent && <div className="form-group">
          <label>Paste the HR email (optional)</label>
          <textarea rows={5} value={f.hr_text} onChange={e => set('hr_text', e.target.value)}
            placeholder="Paste the mail from HR to fill the fields below. The mail itself is not saved — only what you confirm." />
          <button className="btn btn-secondary" style={{ marginTop:6 }} onClick={parse} type="button">
            Fill the fields from this mail
          </button>
          <span style={{ fontSize:11, color:'var(--text2)', marginLeft:8 }}>
            Always check what it picked up before saving.
          </span>
        </div>}

        {!isStudent && <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
          <div className="form-group"><label>First name *</label>
            <input value={f.first_name} onChange={e => set('first_name', e.target.value)} />
            <span style={{ fontSize:11, color:'var(--text2)' }}>
              The mail ID is formed as firstname.lastname.
            </span></div>
          <div className="form-group"><label>Last name</label>
            <input value={f.last_name} onChange={e => set('last_name', e.target.value)} /></div>
          <div className="form-group"><label>Personal email *</label>
            <input value={f.personal_email} onChange={e => set('personal_email', e.target.value)} />
            <span style={{ fontSize:11, color:'var(--text2)' }}>Where the welcome and credentials go.</span></div>
          <div className="form-group"><label>Campus *</label>
            <select value={f.campus_id} onChange={e => set('campus_id', e.target.value)}>
              <option value="">— select —</option>
              {campuses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select></div>
          <div className="form-group"><label>Department</label>
            <select value={f.department_id} onChange={e => set('department_id', e.target.value)}>
              <option value="">— none —</option>
              {depts.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select></div>
          <div className="form-group"><label>Designation</label>
            <input value={f.designation} onChange={e => set('designation', e.target.value)} /></div>
          <div className="form-group"><label>Joining date</label>
            <input type="date" value={f.joining_date} onChange={e => set('joining_date', e.target.value)} /></div>
          <div className="form-group"><label>Phone</label>
            <input value={f.phone} onChange={e => set('phone', e.target.value)} /></div>
          <div className="form-group"><label>HR reference</label>
            <input value={f.hr_reference} onChange={e => set('hr_reference', e.target.value)} placeholder="mail subject / ref" /></div>
        </div>}

        {!isStudent && <label className="opt" style={{ margin:'4px 0 8px' }}>
          <input type="checkbox" checked={f.is_replacement} onChange={e => set('is_replacement', e.target.checked)} />
          <span>This is a replacement for someone leaving</span>
        </label>}
        {!isStudent && f.is_replacement && (
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
            <div className="form-group"><label>Outgoing staff name</label>
              <input value={f.outgoing_name} onChange={e => set('outgoing_name', e.target.value)} /></div>
            <div className="form-group"><label>Outgoing account</label>
              <input value={f.outgoing_email} onChange={e => set('outgoing_email', e.target.value)} /></div>
          </div>
        )}

        {err && <div style={{ color:'var(--red)', fontSize:13, marginBottom:8 }}>{err}</div>}
        <div className="flex-gap" style={{ gap:8, justifyContent:'flex-end' }}>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={busy}>{busy?'…':'Create request'}</button>
        </div>
      </div>
    </div>
  );
}

// ── Student's laptop ──────────────────────────────────────────────────────
function LaptopForm({ id, editable, onSaved, setMsg }) {
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k,v) => setF(p => ({ ...p, [k]:v }));

  useEffect(() => {
    api.get(`/onboarding/${id}/laptop`).then(r => {
      const d = r.data[0] || {};
      setF({ brand:d.brand||'', model:d.model||'', serial_number:d.serial_number||'',
        os_version:d.os_version||'', os_license_key:d.os_license_key||'',
        ms_office_license:d.ms_office_license||'', mac_address:d.mac_address||'',
        charger_serial:d.charger_serial||'', recorded_at:d.recorded_at, recorded_by_name:d.recorded_by_name });
    }).catch(()=>setF({}));
  }, [id]);
  if (!f) return <div style={{ fontSize:12.5, color:'var(--text2)' }}>Loading…</div>;

  const save = async () => {
    setBusy(true); setMsg('');
    try { await api.post(`/onboarding/${id}/laptop`, f); setMsg('✓ Laptop recorded'); onSaved(); }
    catch (e) { setMsg(errText(e, 'Could not save the laptop details')); }
    finally { setBusy(false); }
  };

  const field = (k, label, placeholder, required) => (
    <div className="form-group" style={{ margin:0 }}>
      <label>{label}{required ? ' *' : ''}</label>
      <input value={f[k] || ''} disabled={!editable} placeholder={placeholder}
             onChange={e => set(k, e.target.value)} />
    </div>
  );

  return (<>
    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(200px, 1fr))', gap:10 }}>
      {field('brand','Make','e.g. HP, Dell, Lenovo', true)}
      {field('model','Model','e.g. Pavilion 14')}
      {field('serial_number','Serial number','on the underside of the machine', true)}
      {field('os_version','Operating system','e.g. Windows 11 Home')}
      {field('os_license_key','OS licence key','')}
      {field('ms_office_license','MS Office licence','')}
      {field('mac_address','MAC address','00:1A:2B:3C:4D:5E')}
      {field('charger_serial','Charger serial','if marked')}
    </div>
    {f.recorded_at && (
      <div style={{ fontSize:11.5, color:'var(--text2)', marginTop:8 }}>
        Recorded {dt(f.recorded_at)}{f.recorded_by_name ? ` by ${f.recorded_by_name}` : ''}.
      </div>
    )}
    {editable && (
      <button className="btn btn-primary" style={{ marginTop:10 }} disabled={busy}
        onClick={save}>{busy ? 'Saving…' : 'Save laptop details'}</button>
    )}
  </>);
}

// ── Staff profile ─────────────────────────────────────────────────────────
// One page for a person: who they are, which Google groups they are in, and
// what the school has issued them. Shown from the staff list and from the
// onboarding record, and it reads the same whether or not they have a portal
// login — the login is just another fact about the same person.
export function StaffProfile({ staffId, onBack }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    api.get(`/onboarding/staff/${staffId}`).then(r => setD(r.data))
       .catch(e => setErr(errText(e, 'Could not load this person')));
  }, [staffId]);

  if (err) return <div><button className="btn btn-secondary" onClick={onBack}>← Back</button>
    <div className="card" style={{ marginTop:12, color:'var(--red)' }}>{err}</div></div>;
  if (!d) return <div style={{ padding:40, color:'var(--text2)' }}>Loading…</div>;

  const s = d.staff;
  const row = (k, v) => (
    <div style={{ padding:'6px 0', borderBottom:'1px solid var(--border)' }}>
      <div style={{ fontSize:11.5, color:'var(--text2)' }}>{k}</div>
      <div style={{ fontSize:13.5, marginTop:1 }}>{v || <span style={{ color:'var(--text3)' }}>—</span>}</div>
    </div>
  );
  const held = d.assets.filter(a => !a.returned_on);
  const returned = d.assets.filter(a => a.returned_on);

  return (
    <div>
      {onBack && <button className="btn btn-secondary" style={{ marginBottom:12 }} onClick={onBack}>← Back</button>}

      <div className="card" style={{ marginBottom:14 }}>
        <h2 style={{ fontSize:19, fontWeight:600, marginBottom:2 }}>{s.name}</h2>
        <div style={{ fontSize:13, color:'var(--text2)' }}>
          {s.designation || 'No designation recorded'} · {s.campus_name}
          {!s.active && <span className="badge badge-high" style={{ marginLeft:8 }}>inactive</span>}
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(190px, 1fr))', gap:'0 20px', marginTop:10 }}>
          {row('Employee ID', s.employee_id)}
          {row('School email', s.email)}
          {row('Personal email', s.personal_email)}
          {row('Contact number', s.phone)}
          {row('Portal login', d.portal_login
            ? `${d.portal_login.username} (${d.portal_login.role})`
            : 'Not created yet')}
          {row('Details confirmed', s.details_confirmed_at
            ? new Date(s.details_confirmed_at).toLocaleDateString('en-IN') : 'Not confirmed')}
        </div>
      </div>

      <div className="card" style={{ marginBottom:14 }}>
        <b style={{ fontSize:14 }}>Google groups</b>
        {!d.groups.length
          ? <div style={{ fontSize:12.5, color:'var(--text2)', marginTop:6 }}>
              No group memberships recorded. Groups are recorded when a person is onboarded through the portal.
            </div>
          : <div style={{ marginTop:8 }}>{d.groups.map(g => (
              <div key={g.group_email} className="flex-gap"
                   style={{ justifyContent:'space-between', padding:'5px 0', borderBottom:'1px solid var(--border)', fontSize:13 }}>
                <span className="mono">{g.group_email}</span>
                <span><span className="badge badge-normal" style={{ marginRight:6 }}>{g.kind}</span>
                  <span className={`badge ${g.status==='verified'?'badge-completed':g.status==='failed'?'badge-high':'badge-normal'}`}>{g.status}</span></span>
              </div>
            ))}</div>}
      </div>

      <div className="card" style={{ marginBottom:14 }}>
        <b style={{ fontSize:14 }}>Assets issued</b>
        {!d.assets.length
          ? <div style={{ fontSize:12.5, color:'var(--text2)', marginTop:6 }}>Nothing issued to this person.</div>
          : <div className="table-wrap" style={{ marginTop:8 }}><table>
              <thead><tr><th>Asset</th><th>Type</th><th>Issued</th><th>Acknowledgement</th><th>Status</th></tr></thead>
              <tbody>{[...held, ...returned].map(a => (
                <tr key={a.assignment_id} style={{ opacity:a.returned_on?.55:1 }}>
                  <td style={{ fontSize:12.5 }}><b className="mono">{a.asset_tag}</b>
                    <div style={{ color:'var(--text2)' }}>{a.brand} {a.model}{a.serial_number?` · ${a.serial_number}`:''}</div></td>
                  <td style={{ fontSize:12.5 }}>{a.asset_type}</td>
                  <td style={{ fontSize:12.5 }}>{a.assigned_on ? new Date(a.assigned_on).toLocaleDateString('en-IN') : '—'}
                    {a.returned_on && <div style={{ color:'var(--text2)' }}>returned {new Date(a.returned_on).toLocaleDateString('en-IN')}</div>}</td>
                  <td style={{ fontSize:12 }}>{a.ack_form || <span style={{ color:'var(--text3)' }}>no form</span>}
                    {a.ack_code && <div className="mono" style={{ color:'var(--text2)' }}>{a.ack_code}</div>}</td>
                  <td style={{ fontSize:12 }}>
                    {!a.ack_code ? '—' : (<>
                      <div style={{ color:a.signed_at?'var(--green)':'var(--text2)' }}>
                        {a.signed_at ? '✓ staff signed' : 'staff pending'}</div>
                      {a.requires_it_sign && <div style={{ color:a.it_signed_at?'var(--green)':'var(--text2)' }}>
                        {a.it_signed_at ? '✓ IT head' : 'IT head pending'}</div>}
                      {a.requires_hr_sign && <div style={{ color:a.hr_signed_at?'var(--green)':'var(--text2)' }}>
                        {a.hr_signed_at ? '✓ HR' : 'HR pending'}</div>}
                    </>)}
                  </td>
                </tr>
              ))}</tbody>
            </table></div>}
      </div>

      {d.onboarding && (
        <div className="card">
          <b style={{ fontSize:14 }}>Onboarding</b>
          <div style={{ fontSize:13, marginTop:6 }}>
            <span className="mono">{d.onboarding.ref}</span>
            {' · '}<span className={`badge ${STATUS_CLASS[d.onboarding.status]||'badge-normal'}`}>
              {STATUS_LABEL[d.onboarding.status] || d.onboarding.status}</span>
            {d.onboarding.joining_date && ` · joined ${new Date(d.onboarding.joining_date).toLocaleDateString('en-IN')}`}
            {' · '}policy quiz {d.onboarding.quiz_passed ? 'passed' : 'not passed'}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Detail ────────────────────────────────────────────────────────────────
function Detail({ id, onBack }) {
  const { can } = useAuth();
  const [r, setR] = useState(null);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [pwd, setPwd] = useState(null);
  const [email, setEmail] = useState('');
  const [ouId, setOuId] = useState('');
  const [ous, setOus] = useState([]);
  const [groupMaster, setGroupMaster] = useState([]);
  const [pickGroup, setPickGroup] = useState('');
  const [newGroup, setNewGroup] = useState('');
  const [stock, setStock] = useState({});       // itemId -> assets of that kind

  const load = useCallback(() => api.get(`/onboarding/${id}`).then(x => {
    setR(x.data);
    // Offer the generated address, but only before anything has been typed —
    // never overwrite what the admin has entered.
    setEmail(prev => prev || x.data.suggested_local || '');
  }).catch(()=>{}), [id]);
  useEffect(() => { load(); }, [load]);

  // Only this campus's OUs and groups — the lists themselves are scoped, so a
  // wrong choice is not on offer in the first place.
  useEffect(() => {
    if (!r?.campus_id) return;
    api.get('/gw/ous').then(x => {
      const mine = x.data.filter(o => o.campus_id === r.campus_id && o.active);
      setOus(mine);
      setOuId(p => p || (mine.find(o => o.is_default)?.id || '') );
    }).catch(()=>{});
    api.get('/gw/group-ids', { params:{ campus_id:r.campus_id } })
      .then(x => setGroupMaster(x.data)).catch(()=>{});
  }, [r?.campus_id]);

  // Stock for each asset-backed item, fetched once the item is on screen.
  useEffect(() => {
    (r?.checklist || []).filter(i => i.asset_kind && !i.asset_id && i.applicable).forEach(i => {
      if (stock[i.id]) return;
      api.get(`/onboarding/${id}/stock`, { params:{ kind:i.asset_kind } })
        .then(x => setStock(s => ({ ...s, [i.id]: x.data }))).catch(()=>{});
    });
  }, [r, id, stock]);

  if (!r) return <div style={{ padding:40, color:'var(--text2)' }}>Loading…</div>;

  const act = async (fn, okMsg) => {
    setBusy(true); setMsg('');
    try { const out = await fn(); setMsg(okMsg || '✓ Done'); load(); return out; }
    catch (e) { setMsg(errText(e)); }
    finally { setBusy(false); }
  };
  const editable = can('onboarding','edit') && !r.closed_at;
  const isStudent = r.kind === 'student';
  // Step numbers are counted as the cards render, so hiding Groups for a
  // student gives 1,2,3,4 rather than a gap where step 2 used to be.
  let stepNo = 0;
  const step = () => ++stepNo;

  return (
    <div>
      <button className="btn btn-secondary" style={{ marginBottom:12 }} onClick={onBack}>← All requests</button>

      <div className="card" style={{ marginBottom:14 }}>
        <div className="flex-gap" style={{ justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:10 }}>
          <div>
            <div style={{ fontSize:12, color:'var(--text2)' }} className="mono">{r.ref}</div>
            <h2 style={{ fontSize:19, fontWeight:600, margin:'2px 0 4px' }}>{r.staff_name}</h2>
            <div style={{ fontSize:13, color:'var(--text2)' }}>
              {r.campus_name}{r.department_name ? ` · ${r.department_name}` : ''}
              {r.designation ? ` · ${r.designation}` : ''}
              {r.joining_date ? ` · joins ${new Date(r.joining_date).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'})}` : ''}
            </div>
          </div>
          <span className={`badge ${STATUS_CLASS[r.status] || 'badge-normal'}`} style={{ fontSize:13 }}>
            {STATUS_LABEL[r.status] || r.status}
          </span>
        </div>
        {r.account_simulated && (
          <div style={{ marginTop:10, padding:'7px 10px', background:'var(--bg3)', border:'1px dashed var(--border)',
            borderRadius:6, fontSize:12.5, color:'var(--text2)' }}>
            ⚠ Simulated — this account was <b>not</b> created in Google. Turn simulation off in Settings → Google Workspace for real accounts.
          </div>
        )}
        {msg && <div style={{ marginTop:10, fontSize:13, color: msg.startsWith('✓')?'var(--green)':'var(--red)' }}>{msg}</div>}
      </div>

      {/* 1 — Workspace account */}
      <div className="card" style={{ marginBottom:14 }}>
        <b style={{ fontSize:14 }}>{step()} · Google Workspace account</b>
        {r.workspace_email ? (
          <div style={{ fontSize:13.5, marginTop:8 }}>
            <div><b className="mono">{r.workspace_email}</b></div>
            <div style={{ color:'var(--text2)', fontSize:12.5, marginTop:3 }}>
              OU <span className="mono">{r.ou_path}</span> · created {dt(r.account_created_at)}
            </div>
          </div>
        ) : (<>
          <div style={{ fontSize:12.5, color:'var(--text2)', margin:'6px 0 8px' }}>
            Only this campus's organizational units are offered — an account cannot land in another campus.
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1.2fr 1fr auto', gap:8, alignItems:'end' }}>
            <div className="form-group" style={{ margin:0 }}><label>School email</label>
              {/* The domain is fixed and added by the server, so it cannot be
                  mistyped. The local part stays editable for clashes. */}
              <div className="flex-gap" style={{ gap:0, alignItems:'stretch' }}>
                <input style={{ flex:1, borderTopRightRadius:0, borderBottomRightRadius:0 }}
                       placeholder={r.suggested_local || 'first.last'}
                       value={email} onChange={e => setEmail(e.target.value)} />
                <span style={{ display:'flex', alignItems:'center', padding:'0 10px', fontSize:12.5,
                  background:'var(--bg3)', border:'1px solid var(--border)', borderLeft:'none',
                  borderTopRightRadius:6, borderBottomRightRadius:6, color:'var(--text2)', whiteSpace:'nowrap' }}>
                  @{r.mail_domain || 'domain not set'}
                </span>
              </div>
              {r.suggested_local && email !== r.suggested_local && (
                <span style={{ fontSize:11, color:'var(--text2)' }}>
                  Suggested: <button type="button" onClick={() => setEmail(r.suggested_local)}
                    style={{ background:'none', border:'none', padding:0, font:'inherit',
                             color:'var(--accent)', cursor:'pointer' }}>{r.suggested_local}</button>
                  {isStudent ? ' (from the admission number)' : ' (firstname.lastname)'}
                </span>
              )}</div>
            <div className="form-group" style={{ margin:0 }}><label>Organizational unit</label>
              <select value={ouId} onChange={e => setOuId(e.target.value)}>
                <option value="">— choose —</option>
                {ous.map(o => <option key={o.id} value={o.id}>{o.name}{o.is_default?' (default)':''}</option>)}
              </select></div>
            <button className="btn btn-primary" disabled={busy || !email || !ouId || !editable}
              onClick={() => act(async () => {
                const x = await api.post(`/onboarding/${id}/create-account`,
                  { workspace_email: email, campus_ou_id: ouId });
                setPwd(x.data.temp_password); return x;
              }, '✓ Account recorded')}>Create account</button>
          </div>
          {!ous.length && <div style={{ fontSize:12, color:'var(--red)', marginTop:6 }}>
            No organizational unit is mapped for this campus — add one in Master → Google OU Mapping first.
          </div>}
        </>)}
        {pwd && (
          <div style={{ marginTop:10, padding:10, background:'var(--bg3)', border:'1px solid var(--border)', borderRadius:6 }}>
            <b style={{ fontSize:13 }}>Temporary password — shown once</b>
            <div className="mono" style={{ fontSize:16, margin:'6px 0' }}>{pwd}</div>
            <div style={{ fontSize:11.5, color:'var(--text2)' }}>
              Send this to {r.personal_email} yourself. It is <b>not stored</b> anywhere in the portal and cannot be shown again.
            </div>
          </div>
        )}
      </div>

      {/* 2 — Groups. Staff only: a student gets an email address and nothing else. */}
      {!isStudent && <div className="card" style={{ marginBottom:14 }}>
        <b style={{ fontSize:14 }}>{step()} · Groups</b>
        {!r.groups.length && <div style={{ fontSize:12.5, color:'var(--text2)', marginTop:6 }}>
          Add the groups this person belongs to from the campus Group IDs master.</div>}
        {r.groups.map(g => (
          <div key={g.id} className="flex-gap" style={{ justifyContent:'space-between', alignItems:'center',
            padding:'6px 0', borderBottom:'1px solid var(--border)', fontSize:13 }}>
            <span className="mono">{g.group_email} <span style={{ color:'var(--text2)', fontSize:11 }}>{g.kind}</span></span>
            <span className="flex-gap" style={{ gap:8, alignItems:'center' }}>
              <span className={`badge ${g.status==='verified'?'badge-completed':g.status==='failed'?'badge-high':'badge-normal'}`}>
                {g.status}</span>
              {g.error && <span style={{ fontSize:11.5, color:'var(--red)' }}>{g.error}</span>}
              {editable && g.status !== 'verified' && g.status !== 'added' &&
                <button className="btn btn-secondary" style={{ padding:'2px 8px', fontSize:11 }}
                  onClick={() => act(() => api.delete(`/onboarding/${id}/groups/${g.id}`))}>remove</button>}
            </span>
          </div>
        ))}
        {editable && r.workspace_email && (<>
          <div className="flex-gap" style={{ gap:8, marginTop:10, alignItems:'end' }}>
            <div className="form-group" style={{ margin:0, flex:1 }}>
              <label>Add from Group IDs master</label>
              <select value={pickGroup} onChange={e => setPickGroup(e.target.value)}>
                <option value="">— choose a group —</option>
                {groupMaster
                  .filter(g => !r.groups.some(x => x.group_email === (g.group_email||'').toLowerCase()))
                  .map(g => <option key={g.id} value={g.id}>{g.name} — {g.group_email}</option>)}
              </select>
            </div>
            <button className="btn btn-secondary" disabled={busy || !pickGroup}
              onClick={() => act(async () => { await api.post(`/onboarding/${id}/groups`, { group_ref_id:pickGroup }); setPickGroup(''); })}>Add</button>
          </div>
          {!groupMaster.length && <div style={{ fontSize:11.5, color:'var(--text2)', marginTop:4 }}>
            No groups in the master for this campus — add them in Master → Group IDs.
          </div>}
          <div className="flex-gap" style={{ gap:8, marginTop:8 }}>
            <input placeholder="one-off address (optional)" value={newGroup}
                   onChange={e => setNewGroup(e.target.value)} style={{ flex:1 }} />
            <button className="btn btn-secondary" disabled={busy || !newGroup}
              onClick={() => act(async () => { await api.post(`/onboarding/${id}/groups`, { group_email:newGroup }); setNewGroup(''); })}>Add</button>
            <button className="btn btn-primary" disabled={busy}
              onClick={() => act(() => api.post(`/onboarding/${id}/apply-groups`), '✓ Groups applied and verified')}>
              Apply &amp; verify</button>
          </div>
        </>)}
      </div>}

      {/* 3 — Induction checklist */}
      <div className="card" style={{ marginBottom:14 }}>
        <b style={{ fontSize:14 }}>{step()} · {isStudent ? 'Setup checklist' : 'Induction checklist'}</b>
        <div style={{ fontSize:12, color:'var(--text2)', margin:'4px 0 8px' }}>
          Your confirmation and the staff member's are recorded separately — ticking here does not confirm it for them.
        </div>
        <div className="table-wrap"><table>
          <thead><tr><th>Activity</th><th style={{ width:120 }}>IT confirms</th><th style={{ width:130 }}>Staff confirms</th><th style={{ width:90 }}>N/A</th></tr></thead>
          <tbody>{r.checklist.map(i => (
            <tr key={i.id} style={{ opacity: i.applicable ? 1 : .55 }}>
              <td style={{ fontSize:13 }}>{i.label}
                {!i.applicable && i.na_reason && <div style={{ fontSize:11.5, color:'var(--text2)' }}>N/A — {i.na_reason}</div>}
                {/* Asset-backed items: pick a unit from stock. Issuing it
                    creates the assignment and raises the right form. */}
                {i.applicable && i.asset_kind && (i.asset_id ? (
                  <div style={{ fontSize:11.5, color:'var(--text2)', marginTop:3 }}>
                    <b className="mono">{i.asset_tag}</b> {i.brand} {i.model}
                    {i.serial_number ? ` · ${i.serial_number}` : ''}
                    {i.ack_code && <>
                      {' · '}<span className="badge badge-normal">{i.ack_template_name}</span>
                      {' '}<span style={{ color: i.signed_at ? 'var(--green)' : 'var(--text2)' }}>
                        {i.signed_at ? 'staff signed' : 'awaiting staff signature'}</span>
                      {i.requires_it_sign && <span style={{ color: i.it_signed_at ? 'var(--green)' : 'var(--text2)' }}>
                        {' · '}{i.it_signed_at ? 'IT head signed' : 'IT head pending'}</span>}
                      {i.requires_hr_sign && <span style={{ color: i.hr_signed_at ? 'var(--green)' : 'var(--text2)' }}>
                        {' · '}{i.hr_signed_at ? 'HR signed' : 'HR pending'}</span>}
                    </>}
                  </div>
                ) : editable && (
                  <div className="flex-gap" style={{ gap:6, marginTop:4 }}>
                    <select style={{ fontSize:12, maxWidth:260 }} defaultValue=""
                      onChange={e => e.target.value &&
                        act(async () => {
                          const x = await api.post(`/onboarding/${id}/checklist/${i.id}/issue`, { asset_id:e.target.value });
                          if (x.data.warning) setMsg(x.data.warning);
                          return x;
                        }, '✓ Issued')}>
                      <option value="">— issue a {i.asset_kind.toLowerCase()} from stock —</option>
                      {(stock[i.id] || []).map(a =>
                        <option key={a.id} value={a.id}>{a.asset_tag} · {a.brand} {a.model}{a.serial_number?` · ${a.serial_number}`:''}</option>)}
                    </select>
                    {stock[i.id] && !stock[i.id].length &&
                      <span style={{ fontSize:11.5, color:'var(--red)' }}>no unassigned {i.asset_kind.toLowerCase()} at this campus</span>}
                  </div>
                ))}</td>
              <td>{i.applicable ? (
                <label style={{ fontSize:12, display:'flex', gap:6, alignItems:'center' }}>
                  <input type="checkbox" checked={i.it_done} disabled={!editable}
                    onChange={e => act(() => api.put(`/onboarding/${id}/checklist/${i.id}`, { it_done: e.target.checked }))} />
                  {i.it_done ? dt(i.it_done_at) : 'pending'}
                </label>) : <span style={{ fontSize:12, color:'var(--text2)' }}>—</span>}</td>
              <td style={{ fontSize:12 }}>{!i.needs_staff ? <span style={{ color:'var(--text2)' }}>not required</span>
                : i.staff_done ? <span style={{ color:'var(--green)' }}>✓ {dt(i.staff_done_at)}</span>
                : <span style={{ color:'var(--text2)' }}>waiting</span>}</td>
              <td>{editable && (i.applicable
                ? <button className="btn btn-secondary" style={{ padding:'2px 8px', fontSize:11 }}
                    onClick={() => { const why = window.prompt(`Why is "${i.label}" not applicable?`); if (why && why.trim())
                      act(() => api.put(`/onboarding/${id}/checklist/${i.id}`, { applicable:false, na_reason:why.trim() })); }}>
                    mark N/A</button>
                : <button className="btn btn-secondary" style={{ padding:'2px 8px', fontSize:11 }}
                    onClick={() => act(() => api.put(`/onboarding/${id}/checklist/${i.id}`, { applicable:true }))}>
                    restore</button>)}</td>
            </tr>
          ))}</tbody>
        </table></div>
        {!isStudent && (
          <div style={{ fontSize:11.5, color:'var(--text2)', marginTop:10 }}>
            The Google Drive folder is created outside the portal — tick it here once you have done it and shared it.
          </div>
        )}
      </div>

      {/* Student laptop — a record, not an issue. The student brings it, so
          nothing leaves stock and there is nothing to sign for. */}
      {isStudent && (
        <div className="card" style={{ marginBottom:14 }}>
          <b style={{ fontSize:14 }}>Student's own laptop</b>
          <div style={{ fontSize:12, color:'var(--text2)', margin:'4px 0 10px' }}>
            The student brings this device — it is not school stock, so nothing is assigned or signed for.
          </div>
          <LaptopForm id={id} editable={editable} onSaved={load} setMsg={setMsg} />
        </div>
      )}

      {/* 4 — Complete / staff progress */}
      <div className="card" style={{ marginBottom:14 }}>
        <b style={{ fontSize:14 }}>{step()} · Hand over to the {isStudent ? "student" : "staff member"}</b>
        {!r.it_completed_at ? (<>
          <div style={{ fontSize:12.5, color:'var(--text2)', margin:'6px 0 10px' }}>
            When you complete your part, the welcome email goes to{' '}
            <b>{r.workspace_email || r.personal_email || 'the account, once it exists'}</b>{' '}
            with their checklist{isStudent ? ' and the IT policy' : ' and the policy quiz'}.
            {r.workspace_email && <> They will need their new account password to open it.</>}
          </div>
          <button className="btn btn-primary" disabled={busy || !editable}
            onClick={() => act(() => api.post(`/onboarding/${id}/complete-it`), '✓ IT part complete — welcome email sent')}>
            Complete IT part &amp; send welcome
          </button>
        </>) : (
          <div style={{ fontSize:13, marginTop:8 }}>
            <div>✓ IT completed {dt(r.it_completed_at)}</div>
            <div style={{ marginTop:4, color: r.school_mail_status === 'sent' ? 'var(--green)' : 'var(--red)' }}>
              Welcome email: {r.school_mail_status || 'not sent'} {r.school_mail_at ? `· ${dt(r.school_mail_at)}` : ''}
              {r.school_mail_error && ` · ${r.school_mail_error}`}
            </div>
            <div style={{ marginTop:4, color:'var(--text2)' }}>
              Staff opened: {r.staff_opened_at ? dt(r.staff_opened_at) : 'not yet'} ·
              checklist: {r.staff_checklist_done_at ? dt(r.staff_checklist_done_at) : 'pending'} ·
              quiz: {r.quiz_passed ? `passed ${dt(r.quiz_passed_at)}` : `${r.quiz_attempts.length} attempt(s)`}
            </div>
            {editable && <button className="btn btn-secondary" style={{ marginTop:8 }} disabled={busy}
              onClick={() => act(() => api.post(`/onboarding/${id}/resend-welcome`), '✓ Welcome email resent')}>
              Resend welcome email</button>}
          </div>
        )}
      </div>

      {/* 5 — Close */}
      <div className="card" style={{ marginBottom:14 }}>
        <b style={{ fontSize:14 }}>{step()} · Close the request</b>
        {r.closed_at ? (
          <div style={{ fontSize:13, marginTop:8, color:'var(--green)' }}>✓ Closed {dt(r.closed_at)}</div>
        ) : (<>
          <div style={{ fontSize:12.5, color:'var(--text2)', margin:'6px 0 10px' }}>
            Available only when every mandatory task, both confirmations and the quiz pass are recorded. The server
            re-checks this — the button being enabled is not what decides it.
          </div>
          <button className={`btn ${r.status==='ready_to_close'?'btn-primary':'btn-secondary'}`}
            disabled={busy || !editable} onClick={() => act(() => api.post(`/onboarding/${id}/close`), '✓ Closed')}>
            Close request
          </button>
        </>)}
      </div>

      {/* Delete. Deliberately last, quiet, and explicit about what it leaves
          behind — a Google account the portal cannot remove. */}
      {can('onboarding','edit') && (
        <div className="card" style={{ marginBottom:14, borderColor:'var(--border)' }}>
          <b style={{ fontSize:14 }}>Delete this request</b>
          <div style={{ fontSize:12, color:'var(--text2)', margin:'4px 0 10px' }}>
            Removes the record, its checklist, groups and quiz attempts, and returns any issued
            asset to stock. {r.workspace_email && !r.account_simulated
              ? <b>It does not delete the Google account — that has to be done in the Admin console.</b>
              : 'Signed acknowledgements are never deleted.'}
          </div>
          <button className="btn btn-secondary" style={{ color:'var(--red)', borderColor:'var(--red)' }}
            disabled={busy}
            onClick={async () => {
              if (!window.confirm(`Delete ${r.ref} — ${r.staff_name}?`)) return;
              setBusy(true); setMsg('');
              try {
                const x = await api.delete(`/onboarding/${id}`);
                if (x.data.note) window.alert(x.data.note);
                onBack();
              } catch (e) {
                const d = e.response?.data;
                if (d?.error === 'live_account') {
                  // Second, deliberate confirmation naming the real account.
                  if (window.confirm(`${d.message}\n\nDelete the portal record anyway?`)) {
                    try {
                      const x2 = await api.delete(`/onboarding/${id}`, { data:{ confirm_live:true } });
                      if (x2.data.note) window.alert(x2.data.note);
                      onBack(); return;
                    } catch (e2) { setMsg(errText(e2, 'Could not delete')); }
                  }
                } else setMsg(errText(e, 'Could not delete'));
              } finally { setBusy(false); }
            }}>Delete request</button>
        </div>
      )}

      {/* Audit */}
      <div className="card">
        <b style={{ fontSize:14 }}>Audit trail</b>
        <div style={{ marginTop:8, maxHeight:280, overflowY:'auto' }}>
          {r.events.map(e => (
            <div key={e.id} style={{ fontSize:12.5, padding:'4px 0', borderBottom:'1px solid var(--border)' }}>
              <span style={{ color:'var(--text2)' }}>{dt(e.created_at)}</span>
              {' · '}<span className="badge badge-normal">{e.actor}</span>
              {' '}{e.event.replace(/_/g,' ')}
              {e.user_name ? ` · ${e.user_name}` : ''}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
